import { Timestamp } from '@angular/fire/firestore';

export type ChurchAffiliationInviteStatus =
  | 'pending'
  | 'accepted'
  | 'rejected'
  | 'cancelled'
  | 'expired'
  | 'superseded' // another org's invite was accepted first
  | 'ended'; // church left or was removed after joining

/**
 * Either an invitation from a management organization to an existing church group to join its
 * org chart under `targetNodeId` (direction 'invite'), or a church's request to join an
 * organization (direction 'request'), where the organization picks `targetNodeId` on approval.
 * Doc id: `${organizationId}_${churchId}`. Written only by the churchAffiliation_* Cloud Functions.
 */
export interface ChurchAffiliationInvite {
  id: string;
  direction?: 'invite' | 'request'; // missing on invites created before join requests existed
  organizationId: string;
  organizationName: string;
  organizationLogoUrl?: string;

  churchId: string;
  churchName: string;
  churchImageUrl?: string;
  churchCity?: string;

  // Where the church will be placed; empty on a join request until the organization approves it
  targetNodeId: string;
  targetNodeName: string;
  targetLevelName?: string;
  targetNodePath: string;
  targetAncestors: string[];
  targetLineage?: string[]; // display names from the root down to the target

  status: ChurchAffiliationInviteStatus;
  message?: string;
  invitedBy?: { uid: string; name: string }; // invitations
  requestedBy?: { uid: string; name: string }; // join requests

  respondedBy?: string;
  respondedAt?: Timestamp;
  rejectionReason?: string;

  created: Timestamp;
  modified: Timestamp;
  expiresAt: Timestamp;
}

// Church data an affiliated organization can see; finances are never shared
export const CHURCH_AFFILIATION_SHARED_STATS = ['attendance', 'growth'] as const;

// Id of the synced role created on a church node for the church's own admins
export const CHURCH_ADMIN_ROLE_ID = 'church_admin';

export function isJoinRequest(invite: ChurchAffiliationInvite): boolean {
  return invite.direction === 'request';
}
